import {
  Button,
  Center,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Select,
  Skeleton,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBoolean,
} from "@chakra-ui/react"
import { isAddress } from "@ethersproject/address"
import { BigNumber } from "ethers"
import { useFormik } from "formik"
import flatten from "lodash.flatten"
import type { FC } from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import AccountCardBio from "@/common/components/AccountCardBio"
import AccountCardDetailed from "@/common/components/AccountCardDetailed"
import AccountCardKarmaScore from "@/common/components/AccountCardKarmaScore"
import SmallDelegateUserIdentity from "@/common/components/columns/SmallDelegateUserIdentity"
import LoadMoreButton from "@/common/components/LoadMoreButton"
import { ROUTES } from "@/common/constants/routes"
import { getWeightLabel } from "@/common/helpers/bignumber"
import { getChainMediumName } from "@/common/helpers/fetch"
import { useDevice } from "@/common/hooks/useDevice"
import { useRouter } from "@/common/hooks/useRouter"
import { getAddressFromEns } from "@/createProposal/utils/ens"
import DelegateButton from "@/delegation/components/DelegateButton"
import useKarmaScores from "@/governance/hooks/useKarmaScores"
import { getOrgCustomLabel } from "@/organization/helpers/org-custom-label"
import { getOrganizationIssues } from "@/organization/helpers/organization"
import {
  FeatureFlag,
  useOrganization,
} from "@/organization/providers/OrganizationProvider"
import type {
  Account,
  AccountId,
  Delegate,
  DelegatesFiltersInput,
  DelegatesQuery,
  DelegatesQueryVariables,
  Governor,
  Organization,
} from "@/query/graphql"
import {
  DelegatesDocument,
  DelegatesSortBy,
  useOrganizationIssuesQuery,
} from "@/query/graphql"
import { useInfiniteCursorQuery } from "@/query/hooks/useInfiniteQuery"
import EligibleBadge from "@/stake/components/staker/EligibleBadge"
import { spacing } from "@/theme/foundations/spacing"
import CrossMark from "@/ui/components/icons/CrossMark"
import FilterIcon from "@/ui/components/icons/FilterIcon"
import { DiagramCells } from "@/ui/components/icons/font-awesome/DiagramCells"
import { Grid as GridIcon } from "@/ui/components/icons/font-awesome/Grid"
import { ListOl } from "@/ui/components/icons/font-awesome/ListOl"
import MagnifyingGlass from "@/ui/components/icons/MagnifyingGlass"
import { shouldShowActiveDelegates } from "@/utils/shouldShowActiveDelegates"
import type { ChainId } from "@/web3/constants/chains"
import { isSolanaAddress } from "@/web3/helpers/address"
import { getWhiteLabelRoute } from "@/whitelabel/utils/breadcrumb"

enum ViewMode {
  CARD_VIEW = "CARD_VIEW",
  TABLE_VIEW = "TABLE_VIEW",
  BIO_VIEW = "BIO_VIEW",
}

enum DelegateFilterMode {
  ALL = "ALL",
  SEEKING_FOR_DELEGATIONS = "SEEKING_FOR_DELEGATIONS",
}

type IssueFilter = number | undefined

type GovernorIdFilter = AccountId | undefined

const SortModes = [
  { label: "Voting Power", value: DelegatesSortBy.Votes, tokenRequired: true },
  {
    label: "Received Delegations",
    value: DelegatesSortBy.Delegators,
    tokenRequired: true,
  },
  { label: "Random", value: DelegatesSortBy.Id, tokenRequired: false },
]

export type OnDelegateSelected = (
  delegate: string,
  data?: { drs: number; isEligible: boolean },
) => void

type Props = {
  governorSlug: string
  organization: Organization
  governors?: Governor[]
  onSelectAccount?: (account: Account) => void
  onDelegateSelected?: OnDelegateSelected
  displayProfileLink?: boolean
  isStakeMode?: boolean
  displayChangeView?: boolean
  changeUrlOnSearch?: boolean
  isWhiteLabel: boolean
  karmaName?: string | undefined
  minKarmaScoreEligible?: number
  disableSelectNonEligible?: boolean
}
const GovernanceDelegatesExplore: FC<Props> = ({
  governorSlug,
  organization,
  governors,
  displayProfileLink = true,
  displayChangeView = true,
  changeUrlOnSearch = true,
  isStakeMode = false,
  minKarmaScoreEligible,
  onSelectAccount,
  onDelegateSelected,
  isWhiteLabel,
  karmaName,
  disableSelectNonEligible = false,
}) => {
  const { onLittleDevice, onLargeDevice } = useDevice()
  const router = useRouter()
  const { isFeatureFlagOn } = useOrganization()
  const isSortByPrioritizedEnabled = isFeatureFlagOn(
    FeatureFlag.DelegatesSortedByPriority,
  )

  const governanceId = governors?.[0]?.id ?? ""

  const { query } = router

  const [view, setView] = useState<ViewMode>(ViewMode.CARD_VIEW)
  const [issueFilter, setIssueFilter] = useState<IssueFilter>(
    (query.issueFilter as unknown as number) ?? undefined,
  )
  const [governorIdFilter, setGovernorIdFilter] = useState<GovernorIdFilter>(
    query.governorIdFilter ?? undefined,
  )
  const [delegateFilter, setDelegateFilter] = useState<DelegateFilterMode>(
    query.delegateFilter
      ? (query.delegateFilter.toUpperCase() as DelegateFilterMode)
      : DelegateFilterMode.ALL,
  )

  const defaultSort = useMemo(() => {
    if (query?.sort) {
      const validSortValues = Object.values(DelegatesSortBy)

      if (validSortValues.includes(query.sort as DelegatesSortBy)) {
        return query.sort as DelegatesSortBy
      }
    }

    return isSortByPrioritizedEnabled
      ? DelegatesSortBy.IsPrioritized
      : DelegatesSortBy.Votes
  }, [isSortByPrioritizedEnabled, query.sort])

  const [sort, setSort] = useState<DelegatesSortBy>(defaultSort)
  const [addressOrEnsFilter, setAddressOrEnsFilter] = useState<
    string | undefined
  >(undefined)

  useEffect(() => {
    setSort(defaultSort)
  }, [defaultSort])

  const firstRender = useRef(true)

  const getFilterQuery = (): DelegatesFiltersInput | null => {
    const isSeekingDelegation = Boolean(
      delegateFilter === DelegateFilterMode.SEEKING_FOR_DELEGATIONS,
    )

    return {
      isSeekingDelegation: isSeekingDelegation ?? undefined,
      issueIds: issueFilter ? [issueFilter as any] : undefined,
      governorId: Boolean(governorIdFilter) ? governorIdFilter : undefined,
      organizationId: organization.id,
    }
  }

  const filterQuery = getFilterQuery()

  // biome-ignore lint/correctness/useExhaustiveDependencies: biome-migration
  useEffect(() => {
    if (!sort && !delegateFilter && !issueFilter && !governorIdFilter) {
      return
    }

    if (firstRender.current) {
      firstRender.current = false

      return
    }

    if (changeUrlOnSearch) {
      router.replace({
        pathname: isWhiteLabel
          ? getWhiteLabelRoute(ROUTES.governance.delegates(governorSlug))
          : ROUTES.governance.delegates(governorSlug),
        query: {
          sort: sort,
          fieldFilter: delegateFilter?.toLocaleLowerCase(),
          issueFilter,
          governorIdFilter,
        },
      })
    }
  }, [sort, delegateFilter, issueFilter, governorIdFilter])

  const handleSortChange = (value: DelegatesSortBy) => setSort(value)

  const onAddressOrEnsFilterChange = (value: string) => {
    setAddressOrEnsFilter(value)
  }

  const handleGovernorIdFilterChange = (governorId: AccountId) => {
    // Delegates query doesn't support filtering by governorId and also sort by isPrioritized
    if (governorId && sort === DelegatesSortBy.IsPrioritized) {
      handleSortChange(DelegatesSortBy.Votes)
    }

    setGovernorIdFilter(governorId)
  }

  const handleDelegateFilterChange = (value: string) => {
    setDelegateFilter(value as DelegateFilterMode)
  }

  const handleIssueFilterChange = (value?: string) => {
    setIssueFilter(value as unknown as IssueFilter)
  }

  const handleViewChange = (value: ViewMode) => {
    setView(value)
  }

  return (
    <Stack direction="column" spacing={spacing.containerGap} w="full">
      <HStack display={onLittleDevice}>
        <DelegatesSearchBoxMobile
          addressOrEnsFilter={addressOrEnsFilter}
          delegateFilter={delegateFilter}
          governorIdFilter={governorIdFilter}
          governors={governors}
          issueFilter={issueFilter}
          organization={organization}
          sort={sort}
          onAddressOrEnsFilterChange={onAddressOrEnsFilterChange}
          onDelegateFilterChange={handleDelegateFilterChange}
          onGovernorIdFilterChange={handleGovernorIdFilterChange}
          onIssueFilterChange={handleIssueFilterChange}
          onSortChange={handleSortChange}
        />
      </HStack>

      <HStack display={onLargeDevice} justifyContent="flex-end">
        <DelegatesSearchBox
          addressOrEnsFilter={addressOrEnsFilter}
          delegateFilter={delegateFilter}
          displayChangeView={displayChangeView}
          governorIdFilter={governorIdFilter}
          governors={governors}
          issueFilter={issueFilter}
          organization={organization}
          sort={sort}
          view={view}
          onAddressOrEnsFilterChange={onAddressOrEnsFilterChange}
          onDelegateFilterChange={handleDelegateFilterChange}
          onGovernorIdFilterChange={handleGovernorIdFilterChange}
          onIssueFilterChange={handleIssueFilterChange}
          onSortChange={handleSortChange}
          onViewChange={handleViewChange}
        />
      </HStack>

      {sort ? (
        <GovernanceDelegatesList
          addressOrEnsFilter={addressOrEnsFilter}
          disableSelectNonEligible={disableSelectNonEligible}
          displayProfileLink={displayProfileLink}
          filterQuery={filterQuery}
          governanceId={governanceId}
          governors={governors}
          isStakeMode={isStakeMode}
          isWhiteLabel={isWhiteLabel}
          karmaName={karmaName}
          minKarmaScoreEligible={minKarmaScoreEligible}
          organization={organization}
          sort={sort}
          view={view}
          onDelegateSelected={onDelegateSelected}
          onSelectAccount={onSelectAccount}
        />
      ) : (
        <Skeletons />
      )}
    </Stack>
  )
}

export default GovernanceDelegatesExplore

const Skeletons = () => (
  <>
    {new Array(12).fill(0).map((_, index) => (
      <Skeleton key={index} borderRadius="lg" height="200px" />
    ))}
  </>
)

const TableSkeletons = () => (
  <Stack mt={2} spacing={2}>
    {new Array(6).fill(0).map((_, index) => (
      <Skeleton key={index} borderRadius="lg" height="70px" />
    ))}
  </Stack>
)

type DelegatesSearchBoxMobileProps = {
  organization: Organization
  sort: DelegatesSortBy
  delegateFilter: DelegateFilterMode | undefined
  addressOrEnsFilter: string | undefined
  governorIdFilter: AccountId | undefined
  governors?: Governor[]
  onIssueFilterChange: (value?: string) => void
  onGovernorIdFilterChange: (value: string) => void
  onDelegateFilterChange: (value: string) => void
  onSortChange: (value: DelegatesSortBy) => void
  onAddressOrEnsFilterChange: (value: string) => void
  issueFilter: IssueFilter
}
const DelegatesSearchBoxMobile: FC<DelegatesSearchBoxMobileProps> = ({
  organization,
  sort,
  delegateFilter,
  addressOrEnsFilter,
  governors,
  governorIdFilter,
  onGovernorIdFilterChange,
  onIssueFilterChange,
  onDelegateFilterChange,
  onSortChange,
  issueFilter,
  onAddressOrEnsFilterChange,
}) => {
  const { isMultiChain, isFeatureFlagOn } = useOrganization()
  const { data } = useOrganizationIssuesQuery({
    input: { filters: { organizationId: organization.id } },
  })

  const isSortByPrioritizedEnabled = isFeatureFlagOn(
    FeatureFlag.DelegatesSortedByPriority,
  )
  const hasToken = organization?.tokenIds.length > 0
  const displaySortOption = isSortByPrioritizedEnabled || hasToken

  const issues = data?.issues
    ? getOrganizationIssues(
        data.issues,
        isFeatureFlagOn(FeatureFlag.CustomFocusAreasOnly),
      )
    : undefined

  const isAddressOrEnsSearch = Boolean(addressOrEnsFilter)

  return (
    <Stack spacing={spacing.containerGap} width="100%">
      <Divider />
      <Menu closeOnSelect>
        <Flex alignItems="center" gap={spacing.containerGap} width="full">
          <ENSSearchBar
            value={addressOrEnsFilter}
            onAddressOrEnsFilterChange={onAddressOrEnsFilterChange}
          />
          <MenuButton
            as={Button}
            backgroundColor="white"
            border="1px"
            borderColor="gray.200"
            borderRadius="lg"
            flex="1"
            isDisabled={isAddressOrEnsSearch}
          >
            <HStack alignItems="center" justifyContent="center" spacing={2}>
              <FilterIcon />
            </HStack>
          </MenuButton>
        </Flex>

        <MenuList minWidth="240px">
          {shouldShowActiveDelegates(organization) && (
            <MenuOptionGroup
              defaultValue={delegateFilter}
              title={getOrgCustomLabel(organization.id, "Active delegate")}
              type="radio"
              value={delegateFilter}
            >
              <MenuItemOption
                value={DelegateFilterMode.ALL}
                onClick={() => onDelegateFilterChange(DelegateFilterMode.ALL)}
              >
                All Delegates
              </MenuItemOption>
              <MenuItemOption
                value={DelegateFilterMode.SEEKING_FOR_DELEGATIONS}
                onClick={() =>
                  onDelegateFilterChange(
                    DelegateFilterMode.SEEKING_FOR_DELEGATIONS,
                  )
                }
              >
                {getOrgCustomLabel(organization.id, "Active delegates")} only
              </MenuItemOption>
            </MenuOptionGroup>
          )}

          {isMultiChain && governors && governors?.length > 1 ? (
            <>
              <MenuDivider />
              <MenuOptionGroup
                defaultValue={governorIdFilter as unknown as AccountId}
                title="Chains"
                type="radio"
                value={governorIdFilter as unknown as AccountId}
              >
                <MenuItemOption
                  value=""
                  onClick={() => onGovernorIdFilterChange("")}
                >
                  All Chains
                </MenuItemOption>
                {governors?.map((governor) => (
                  <MenuItemOption
                    key={`gov-${governor?.id}`}
                    value={governor?.id}
                    onClick={() => onGovernorIdFilterChange(governor?.id)}
                  >
                    {getChainMediumName(governor.chainId as unknown as ChainId)}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
            </>
          ) : null}

          {issues?.length ? (
            <>
              <MenuDivider />
              <MenuOptionGroup
                defaultValue={issueFilter as unknown as string}
                title="Issues"
                type="radio"
                value={issueFilter as unknown as string}
              >
                <MenuItemOption value="" onClick={() => onIssueFilterChange()}>
                  All Focus Areas
                </MenuItemOption>
                {issues?.map((issue) => (
                  <MenuItemOption
                    key={issue?.id}
                    value={(issue?.id as unknown as string) ?? ""}
                    onClick={() => onIssueFilterChange(issue?.id)}
                  >
                    {issue?.name}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
            </>
          ) : null}

          {displaySortOption ? (
            <>
              <MenuDivider />

              <MenuOptionGroup
                defaultValue={sort}
                title="Sort by"
                type="radio"
                value={sort}
              >
                {isSortByPrioritizedEnabled ? (
                  <MenuItemOption
                    isDisabled={Boolean(governorIdFilter)}
                    value={DelegatesSortBy.IsPrioritized}
                    onClick={() => onSortChange(DelegatesSortBy.IsPrioritized)}
                  >
                    Featured
                  </MenuItemOption>
                ) : null}
                {SortModes.filter(
                  ({ tokenRequired }) =>
                    !tokenRequired || tokenRequired === hasToken,
                ).map(({ value, label }, idx) => (
                  <MenuItemOption
                    key={`sort-${idx}`}
                    value={value}
                    onClick={() => onSortChange(value)}
                  >
                    {label}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
            </>
          ) : null}
        </MenuList>
      </Menu>
    </Stack>
  )
}

type DelegatesSearchBoxProps = {
  organization: Organization
  view: ViewMode
  onViewChange: (value: ViewMode) => void
  sort: DelegatesSortBy
  governors?: Governor[]
  onSortChange: (value: DelegatesSortBy) => void
  delegateFilter: DelegateFilterMode | undefined
  addressOrEnsFilter: string | undefined
  issueFilter: IssueFilter
  governorIdFilter: GovernorIdFilter
  onDelegateFilterChange: (value: string) => void
  onIssueFilterChange: (value: string) => void
  onGovernorIdFilterChange: (governorId: AccountId) => void
  onAddressOrEnsFilterChange: (value: string) => void
  displayChangeView: boolean
}
const DelegatesSearchBox: FC<DelegatesSearchBoxProps> = ({
  organization,
  view,
  onViewChange,
  sort,
  onSortChange,
  governors,
  delegateFilter,
  addressOrEnsFilter,
  issueFilter,
  governorIdFilter,
  onDelegateFilterChange,
  onGovernorIdFilterChange,
  onIssueFilterChange,
  onAddressOrEnsFilterChange,
  displayChangeView,
}) => {
  const { isMultiChain, isFeatureFlagOn } = useOrganization()
  const [isSearchByAddressOrEnsActive, setIsSearchByAddressOrEnsActive] =
    useState(Boolean(addressOrEnsFilter))

  const isSortByPrioritizedEnabled = isFeatureFlagOn(
    FeatureFlag.DelegatesSortedByPriority,
  )

  const getViewIcon = () => {
    switch (view) {
      case ViewMode.CARD_VIEW:
        return GridIcon
      case ViewMode.TABLE_VIEW:
        return ListOl
      case ViewMode.BIO_VIEW:
        return DiagramCells

      default:
        return GridIcon
    }
  }

  const { data } = useOrganizationIssuesQuery({
    input: { filters: { organizationId: organization.id } },
  })

  const issues = data?.issues
    ? getOrganizationIssues(
        data.issues,
        isFeatureFlagOn(FeatureFlag.CustomFocusAreasOnly),
      )
    : undefined

  const isAddressOrEnsSearch = Boolean(addressOrEnsFilter)

  const hasToken = organization?.tokenIds.length > 0
  const displaySortOption = isSortByPrioritizedEnabled || hasToken
  const canDelegate = hasToken

  return (
    <HStack
      justifyContent="flex-end"
      spacing={spacing.containerGap}
      width="100%"
    >
      <ENSSearchBar
        isActive={isSearchByAddressOrEnsActive}
        value={addressOrEnsFilter}
        onAddressOrEnsFilterChange={onAddressOrEnsFilterChange}
        onFocus={(value: boolean) => setIsSearchByAddressOrEnsActive(value)}
      />
      {!isSearchByAddressOrEnsActive ? (
        <>
          {issues?.length ? (
            <Select
              bgColor="white"
              color="gray.600"
              disabled={isAddressOrEnsSearch}
              value={issueFilter}
              width="100%"
              onChange={(e) => onIssueFilterChange(e.target.value)}
            >
              <option value="">All Focus Areas</option>
              {issues.map((issue) => (
                // issue.id must exist, fix this type on BE
                <option key={issue?.id} value={issue?.id ?? ""}>
                  {issue?.name}
                </option>
              ))}
            </Select>
          ) : null}

          {isMultiChain && governors && governors.length > 1 ? (
            <Select
              bgColor="white"
              color="gray.600"
              disabled={isAddressOrEnsSearch}
              value={governorIdFilter}
              width="100%"
              onChange={(e) =>
                onGovernorIdFilterChange(e.target.value as AccountId)
              }
            >
              <option value="">All Chains</option>
              {governors.map((governor) => (
                <option key={`gov-${governor.id}`} value={governor.id}>
                  {getChainMediumName(governor.chainId as unknown as ChainId)}
                </option>
              ))}
            </Select>
          ) : null}
          {shouldShowActiveDelegates(organization) && (
            <Select
              bgColor="white"
              color="gray.600"
              disabled={isAddressOrEnsSearch}
              value={delegateFilter}
              width="100%"
              onChange={(e) => onDelegateFilterChange(e.target.value)}
            >
              <option value={DelegateFilterMode.ALL}>All Delegates</option>
              <option value={DelegateFilterMode.SEEKING_FOR_DELEGATIONS}>
                {getOrgCustomLabel(organization.id, "Active delegates")} only
              </option>
            </Select>
          )}
          {displaySortOption ? (
            <Select
              bgColor="white"
              color="gray.600"
              disabled={isAddressOrEnsSearch}
              value={sort}
              width="100%"
              onChange={(e) => onSortChange(e.target.value as DelegatesSortBy)}
            >
              {isSortByPrioritizedEnabled ? (
                <option
                  disabled={Boolean(governorIdFilter)}
                  value={DelegatesSortBy.IsPrioritized}
                >
                  Sort by: Featured
                </option>
              ) : null}
              {SortModes.filter(
                ({ tokenRequired }) =>
                  !tokenRequired || tokenRequired === hasToken,
              ).map(({ value, label }, idx) => (
                <option key={`sort-${idx}`} value={value}>
                  Sort by: {label}
                </option>
              ))}
            </Select>
          ) : null}

          {displayChangeView ? (
            <Menu>
              <MenuButton as={Button} bgColor="white" variant="secondary">
                <Icon as={getViewIcon()} mr={10} />
              </MenuButton>
              <MenuList>
                <MenuOptionGroup>
                  <MenuItem onClick={() => onViewChange(ViewMode.CARD_VIEW)}>
                    <Text
                      fontWeight={
                        view === ViewMode.CARD_VIEW ? "bold" : "regular"
                      }
                      textStyle="md"
                    >
                      Overview
                    </Text>
                  </MenuItem>
                  {canDelegate ? (
                    <MenuItem onClick={() => onViewChange(ViewMode.TABLE_VIEW)}>
                      <Text
                        fontWeight={
                          view === ViewMode.TABLE_VIEW ? "bold" : "regular"
                        }
                        textStyle="md"
                      >
                        Leaderboard
                      </Text>
                    </MenuItem>
                  ) : null}
                  <MenuItem onClick={() => onViewChange(ViewMode.BIO_VIEW)}>
                    <Text
                      fontWeight={
                        view === ViewMode.BIO_VIEW ? "bold" : "regular"
                      }
                      textStyle="md"
                    >
                      Delegate Statement
                    </Text>
                  </MenuItem>
                </MenuOptionGroup>
              </MenuList>
            </Menu>
          ) : null}
        </>
      ) : null}
    </HStack>
  )
}

type ENSSearchBarProps = {
  onAddressOrEnsFilterChange: (value: string) => void
  value: string | undefined
  isActive?: boolean
  onFocus?: (value: boolean) => void
}

const ENSSearchBar: FC<ENSSearchBarProps> = ({
  onAddressOrEnsFilterChange,
  onFocus,
  isActive,
  value,
}) => {
  const { onLargeDevice, onLittleDevice } = useDevice()

  const formik = useFormik({
    initialValues: { addressOrEns: value },
    onSubmit: (values) => {
      onAddressOrEnsFilterChange(values?.addressOrEns?.trim() ?? "")
    },
  })

  const onClearSearch = () => {
    formik.setValues({ addressOrEns: "" })
    onAddressOrEnsFilterChange("")
    onFocus?.(false)
  }

  return (
    <>
      <Flex
        display={onLargeDevice}
        mr={isActive ? 0 : 4}
        width={isActive ? "100%" : "40px"}
      >
        <form style={{ width: "100%" }} onSubmit={formik.handleSubmit}>
          <InputGroup bgColor="transparent">
            <InputLeftElement mr={8} pl="16px" pointerEvents="none">
              <MagnifyingGlass color="gray.500" />
            </InputLeftElement>
            <Input
              bgColor="white"
              borderRadius="md"
              color="gray.600"
              id="addressOrEns"
              name="addressOrEns"
              placeholder={isActive ? "ENS or address" : undefined}
              size="md"
              value={formik.values.addressOrEns}
              width="100%"
              onChange={formik.handleChange}
              onFocus={() => onFocus?.(true)}
            />
          </InputGroup>
        </form>

        {isActive ? (
          <Button
            bgColor="white"
            display={onLargeDevice}
            ml={2}
            variant="secondary"
            onClick={onClearSearch}
          >
            Cancel
          </Button>
        ) : null}
      </Flex>

      <Flex background="white" display={onLittleDevice} width="100%">
        <form style={{ width: "100%" }} onSubmit={formik.handleSubmit}>
          <InputGroup
            bgColor="transparent"
            justifyContent="flex-end"
            width="100%"
          >
            <InputLeftElement pointerEvents="none">
              <MagnifyingGlass color="gray.500" />
            </InputLeftElement>
            <Input
              bgColor="white"
              borderRadius="md"
              color="gray.600"
              id="addressOrEns"
              name="addressOrEns"
              placeholder="ENS or address"
              size="md"
              value={formik.values.addressOrEns}
              width="100%"
              onChange={formik.handleChange}
              onFocus={() => onFocus?.(true)}
            />
            <InputRightElement pr={1}>
              <Button size="sm" variant="tertiary" onClick={onClearSearch}>
                <Icon as={CrossMark} />
              </Button>
            </InputRightElement>
          </InputGroup>
        </form>
      </Flex>
    </>
  )
}

const PAGE_SIZE = 12
type GovernanceDelegatesListProps = {
  view: ViewMode
  governanceId: string
  sort: DelegatesSortBy
  filterQuery: DelegatesFiltersInput | null
  onSelectAccount?: (account: Account, drs?: number) => void
  displayProfileLink: boolean
  isWhiteLabel: boolean
  isStakeMode: boolean
  onDelegateSelected?: OnDelegateSelected
  addressOrEnsFilter: string | undefined
  organization: Organization
  governors?: Governor[]
  karmaName: string | undefined
  minKarmaScoreEligible?: number
  disableSelectNonEligible?: boolean
}
const GovernanceDelegatesList: FC<GovernanceDelegatesListProps> = ({
  view,
  governanceId,
  sort,
  filterQuery,
  onSelectAccount,
  onDelegateSelected,
  isStakeMode,
  displayProfileLink,
  isWhiteLabel,
  addressOrEnsFilter,
  organization,
  governors,
  karmaName,
  minKarmaScoreEligible,
  disableSelectNonEligible,
}) => {
  const { onLittleDevice, onLargeDevice } = useDevice()
  const [isLoadingSearch, { on, off }] = useBoolean(false)
  const [address, setAddress] = useState<string | undefined>(undefined)
  const isSearchByAddressOrEns = Boolean(address)
  const { isFeatureFlagOn } = useOrganization()
  const displayDelegateButton = !isFeatureFlagOn(FeatureFlag.HideDelegateButton)

  useEffect(() => {
    setAddress(addressOrEnsFilter)
  }, [addressOrEnsFilter])

  // If MultiGov, then don't set a default governorId to filter
  const isMultiGov = governors && governors?.length > 1
  const defaultGovernorId =
    !isMultiGov && governors && governors?.length > 0
      ? governors?.[0]?.id
      : undefined

  // load infinite scroll delegate data
  const {
    data,
    isLoading: isLoadingSection,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteCursorQuery<DelegatesQuery, DelegatesQueryVariables>({
    document: DelegatesDocument,
    sectionName: "Delegates",
    nextPagePath: "delegates",
    variables: {
      input: {
        filters: {
          address:
            address && (isAddress(address) || isSolanaAddress(address))
              ? address
              : undefined,
          governorId: Boolean(filterQuery?.governorId)
            ? filterQuery?.governorId
            : defaultGovernorId,
          isSeekingDelegation: filterQuery?.isSeekingDelegation
            ? true
            : undefined,
          issueIds: filterQuery?.issueIds ?? undefined,
          organizationId: filterQuery?.organizationId ?? organization.id,
        },
        sort: {
          isDescending: true,
          sortBy:
            // Sort by isPriority is not supported if the governorId param is also sent
            (filterQuery?.governorId || defaultGovernorId) &&
            sort === DelegatesSortBy.IsPrioritized
              ? DelegatesSortBy.Votes
              : sort,
        },
        page: { limit: PAGE_SIZE },
      },
    },
  })

  const [delegates, setDelegates] = useState<Delegate[]>([])
  const { karmaScores, karmaScoresFetched, isLoadingKarmaScores } =
    useKarmaScores({ karmaName, delegates })

  // biome-ignore lint/correctness/useExhaustiveDependencies: biome-migration
  useEffect(() => {
    switch (true) {
      case address && !isAddress(address) && !isSolanaAddress(address): {
        on()

        const searchString = address as string

        getAddressFromEns(searchString)
          .then((address) => {
            if (address) {
              setAddress(address)

              return
            }

            // If no data, it means the query failed so an 0x address can be set
            // The API will return no results since this delegate account doesn't exist
            setAddress("0x0000000000000000000000000000000000000000")
          })
          .finally(() => {
            off()
          })

        break
      }
      case Boolean(data): {
        if (data?.pages) {
          const nextDelegates = flatten(
            data.pages
              .filter(
                (page) => (page?.delegates?.nodes as Delegate[])?.length > 0,
              )
              .map((page) => page.delegates.nodes as Delegate[]),
          )

          // TODO(@nicolas): Verify if FeatureFlag.DelegatesSortedByRandom is enabled
          // to then randomize delegates priority first, and then other delegates
          if (sort === DelegatesSortBy.IsPrioritized) {
            const pageNumber = data?.pageParams?.length ?? 1
            const sortedNextDelegates = sortAndShuffleDelegates(
              delegates,
              nextDelegates,
              pageNumber,
            )
            setDelegates(sortedNextDelegates)

            return
          }

          setDelegates(nextDelegates)
        }
        break
      }
      default:
        {
          setDelegates([])
        }

        return
    }
  }, [
    addressOrEnsFilter,
    governanceId,
    data,
    isSearchByAddressOrEns,
    on,
    off,
    address,
    sort,
  ])

  const hasDelegates = delegates ? delegates.length > 0 : false

  const handleLoadMoreClick = () => fetchNextPage()

  const isLoading = isLoadingSearch || isLoadingSection || isFetchingNextPage

  const getAccountDrs = (account: Account) => {
    return (
      karmaScores &&
      karmaScores[account.address.toLowerCase()] &&
      karmaScores[account.address.toLowerCase()].karmaScore
    )
  }

  const isAccountEligible = (account: Account) => {
    const drs = getAccountDrs(account)

    return Boolean(drs) && drs >= (minKarmaScoreEligible ?? 0)
  }

  const getEligibleBadge = (account: Account) => {
    if (
      !isLoadingKarmaScores &&
      Boolean(karmaName) &&
      karmaScores &&
      karmaScoresFetched &&
      minKarmaScoreEligible
    ) {
      const drs = getAccountDrs(account)

      return (
        <EligibleBadge
          isEligible={
            karmaScoresFetched[account.address.toLowerCase()] &&
            Boolean(drs) &&
            drs >= minKarmaScoreEligible
          }
        />
      )
    }

    return undefined
  }

  return (
    <>
      {/* Delegates : As as Table Desktop*/}
      {hasDelegates && view === ViewMode.TABLE_VIEW && (
        <Flex direction="column" display={onLargeDevice}>
          <TableContainer
            background="white"
            border="1px"
            borderColor="gray.200"
            borderRadius="lg"
            width="full"
          >
            <Table>
              <Thead>
                <Tr>
                  <Th maxWidth="5px" minWidth="5px">
                    Rank
                  </Th>
                  <Th>Delegate</Th>
                  <Th>Votes</Th>
                </Tr>
              </Thead>
              <Tbody>
                {delegates?.map((delegate, idx) => {
                  const voteWeight = delegate.votesCount
                  const bigVoteWeight = BigNumber.from(voteWeight)
                  const drs = getAccountDrs(delegate.account)
                  const isEligible = isAccountEligible(delegate.account)
                  const token = delegate?.token

                  const voteWeightFormatted =
                    Boolean(token) && token?.decimals
                      ? getWeightLabel(bigVoteWeight, token.decimals)
                      : undefined

                  return (
                    <Tr key={idx}>
                      <Td maxWidth="5px" minWidth="5px">
                        <Text fontStyle="body.bold.md">{idx + 1}</Text>
                      </Td>
                      <Td>
                        <SmallDelegateUserIdentity
                          account={delegate.account}
                          isWhiteLabel={isWhiteLabel}
                          organization={organization}
                          tokenSymbol={token?.symbol}
                        />
                      </Td>
                      <Td>
                        <Text fontStyle="body.bold.md">
                          {voteWeightFormatted}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        {displayDelegateButton && Boolean(token) ? (
                          isStakeMode ? (
                            <Button
                              fontSize="xs"
                              isDisabled={
                                disableSelectNonEligible && !isEligible
                              }
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                onDelegateSelected &&
                                  onDelegateSelected(delegate.account.address, {
                                    drs,
                                    isEligible,
                                  })
                              }}
                            >
                              Select
                            </Button>
                          ) : null
                        ) : null}
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </TableContainer>
          {isLoading ? <TableSkeletons /> : null}
        </Flex>
      )}

      {/* Delegates : As as Table Mobile*/}
      {hasDelegates && view === ViewMode.TABLE_VIEW && (
        <Flex direction="column" display={onLittleDevice}>
          <TableContainer
            background="white"
            border="1px"
            borderColor="gray.200"
            borderRadius="lg"
            width="full"
          >
            <Table>
              <Thead>
                <Tr>
                  <Th>Delegate</Th>
                  <Th isNumeric>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {delegates.map((delegate, idx) => {
                  const voteWeight = delegate.votesCount
                  const bigVoteWeight = BigNumber.from(voteWeight)
                  const drs = getAccountDrs(delegate.account)
                  const isEligible = isAccountEligible(delegate.account)
                  const token = delegate?.token
                  const voteWeightFormatted =
                    Boolean(token) && token?.decimals
                      ? getWeightLabel(bigVoteWeight, token.decimals)
                      : undefined

                  return (
                    <Tr key={idx}>
                      <Td maxW="2em">
                        <SmallDelegateUserIdentity
                          account={delegate.account}
                          isWhiteLabel={isWhiteLabel}
                          organization={organization}
                          tokenSymbol={token?.symbol}
                          votes={voteWeightFormatted}
                        />
                      </Td>
                      <Td isNumeric>
                        {displayDelegateButton && Boolean(token) ? (
                          isStakeMode ? (
                            <Button
                              fontSize="xs"
                              isDisabled={
                                disableSelectNonEligible && !isEligible
                              }
                              size="sm"
                              variant="secondary"
                              onClick={() =>
                                onDelegateSelected &&
                                onDelegateSelected(delegate.account.address, {
                                  drs,
                                  isEligible,
                                })
                              }
                            >
                              Select
                            </Button>
                          ) : (
                            <DelegateButton
                              account={delegate.account}
                              delegateAddress={delegate.account.address}
                              fontSize="xs"
                              isDisabled={
                                disableSelectNonEligible && !isEligible
                              }
                              organization={organization}
                              pl={5}
                              pr={5}
                              size="sm"
                              variant="secondary"
                            >
                              Delegate
                            </DelegateButton>
                          )
                        ) : null}
                      </Td>
                    </Tr>
                  )
                })}
              </Tbody>
            </Table>
          </TableContainer>
          {isLoading ? <TableSkeletons /> : null}
        </Flex>
      )}

      {/* Delegates : As Cards, Note: View is same for Desktop or Mobile*/}
      {hasDelegates && view === ViewMode.CARD_VIEW && (
        <>
          <Grid
            gap={3}
            gridTemplateColumns={{
              base: "repeat(1, minmax(0, 1fr))",
              md: `repeat(2, minmax(0, 1fr))`,
            }}
          >
            {delegates?.map((delegate, idx) => {
              const drs = getAccountDrs(delegate.account)
              const isEligible = isAccountEligible(delegate.account)

              return (
                <GridItem key={delegate.account?.address ?? idx}>
                  <AccountCardDetailed
                    detailed
                    buttonProps={{
                      isDisabled: disableSelectNonEligible && !isEligible,
                    }}
                    dataQa={`delegatesexplore-delegate-${idx}`}
                    delegate={delegate}
                    displayDelegateButton={displayDelegateButton}
                    displayProfileLink={displayProfileLink}
                    eligibleBadge={getEligibleBadge(delegate.account)}
                    handleDelegateSelected={(account) =>
                      onDelegateSelected &&
                      onDelegateSelected(account, { drs, isEligible })
                    }
                    isStakeMode={isStakeMode}
                    isWhiteLabel={isWhiteLabel}
                    karmaScore={
                      <AccountCardKarmaScore
                        account={delegate.account}
                        hasKarmaScore={Boolean(karmaName)}
                        isLoadingKarmaScores={isLoadingKarmaScores}
                        karmaScores={karmaScores}
                        karmaScoresFetched={karmaScoresFetched}
                      />
                    }
                    organization={organization}
                    onSelectAccount={onSelectAccount}
                  />
                </GridItem>
              )
            })}
            {isLoading ? <Skeletons /> : null}
          </Grid>
        </>
      )}

      {/* Bio View */}
      {hasDelegates && view === ViewMode.BIO_VIEW && (
        <Grid gap={3} minWidth="100%" mt={0} pt={0}>
          {delegates?.map((delegate, idx) => (
            <AccountCardBio
              key={delegate.account?.address ?? idx}
              detailed
              decimals={delegate?.token?.decimals}
              delegate={delegate}
              isWhiteLabel={isWhiteLabel}
              organization={organization}
              tokenSymbol={delegate?.token?.symbol}
            />
          ))}
          {isLoading ? <Skeletons /> : null}
        </Grid>
      )}

      {!hasDelegates && !isLoading && !isFetchingNextPage ? (
        <Center
          background="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          height={12}
          mt={0}
        >
          <Text color="gray.600" fontSize="md">
            {isSearchByAddressOrEns
              ? "No results found. Please search using the complete ENS name or address"
              : "No results found"}
          </Text>
        </Center>
      ) : null}

      {!isSearchByAddressOrEns && hasNextPage && !isLoading ? (
        <Center
          background="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          px={1}
          py={1}
        >
          <LoadMoreButton
            isDisabled={false}
            width="100%"
            onClick={handleLoadMoreClick}
          />
        </Center>
      ) : null}

      {isLoading || isFetchingNextPage ? (
        <Center
          background="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          height={12}
          mt={4}
        >
          <Spinner color="gray.500" ml={3} />
        </Center>
      ) : null}
    </>
  )
}

const sortAndShuffleDelegates = (
  currentDelegates: Delegate[],
  nextDelegates: Delegate[],
  pageNumber: number,
) => {
  const indexMin = (pageNumber - 1) * PAGE_SIZE
  const indexMax = Math.min(indexMin + PAGE_SIZE, nextDelegates.length)

  const shuffleArray = (array: Delegate[]) => {
    return array
      .map((value) => ({ value, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ value }) => value)
  }

  const beforeShuffle = currentDelegates.slice(0, indexMin)
  const delegatesToShuffle = nextDelegates.slice(indexMin, indexMax)
  const shuffledDelegates = shuffleArray(delegatesToShuffle)
  const afterShuffle = nextDelegates.slice(indexMax)

  return [...beforeShuffle, ...shuffledDelegates, ...afterShuffle]
}
